/**
 * 글 본문에 딸린 <style> CSS를 본문 컨테이너 안으로 가둔다.
 *
 * 본문을 Shadow DOM에 넣으면 스타일은 격리되지만 클라이언트에서만 그려져
 * 크롤러(특히 네이버 Yeti)가 본문을 읽지 못한다. 라이트 DOM으로 SSR하면서
 * 글 전용 스타일이 사이트 전역에 새지 않도록 셀렉터마다 scope를 앞에 붙인다.
 *
 * - 일반 규칙: `.a, p` → `S .a, S p`
 * - html·body·:root 로 시작하는 셀렉터: Shadow DOM에서도 아무것도 가리키지 않던
 *   것이라 버린다(예: 노션 내보내기의 `body { max-width: 900px }`).
 * - @media·@supports·@container·@layer 블록: 안쪽 규칙을 재귀로 처리한다.
 * - @import·@charset: 버린다. 그 밖의 at-rule(@font-face·@keyframes 등)은 그대로 둔다.
 */
export function scopeCss(css: string, scope: string): string {
  const source = css.replace(/\/\*[\s\S]*?\*\//g, '');
  let output = '';
  let index = 0;

  while (index < source.length) {
    const braceIndex = source.indexOf('{', index);
    const semicolonIndex = source.indexOf(';', index);

    // `{` 보다 앞선 `;` 는 블록 없는 at-rule(@import 등)이다.
    if (
      semicolonIndex !== -1 &&
      (braceIndex === -1 || semicolonIndex < braceIndex)
    ) {
      index = semicolonIndex + 1;
      continue;
    }
    if (braceIndex === -1) break;

    const closeIndex = findMatchingBrace(source, braceIndex);
    const prelude = source.slice(index, braceIndex).trim();
    const body = source.slice(braceIndex + 1, closeIndex);
    index = closeIndex + 1;

    if (!prelude) continue;

    if (prelude.startsWith('@')) {
      const isGroupingRule = /^@(media|supports|container|layer)\b/i.test(
        prelude,
      );
      output += isGroupingRule
        ? `${prelude}{${scopeCss(body, scope)}}`
        : `${prelude}{${body}}`;
      continue;
    }

    const selectors = splitSelectors(prelude)
      .filter((selector) => !/^(html|body|:root)\b/i.test(selector))
      .map((selector) => `${scope} ${selector}`);
    if (selectors.length > 0) {
      output += `${selectors.join(', ')}{${body}}`;
    }
  }

  return output;
}

/** 여는 중괄호에 대응하는 닫는 중괄호 위치. 짝이 없으면 문자열 끝. */
function findMatchingBrace(source: string, openIndex: number): number {
  let depth = 0;
  for (let i = openIndex; i < source.length; i++) {
    if (source[i] === '{') depth++;
    else if (source[i] === '}' && --depth === 0) return i;
  }
  return source.length;
}

/** 셀렉터 목록을 쉼표로 나눈다. `:is(a, b)` 같은 괄호 안 쉼표는 나누지 않는다. */
function splitSelectors(prelude: string): string[] {
  const selectors: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < prelude.length; i++) {
    const char = prelude[i];
    if (char === '(' || char === '[') depth++;
    else if (char === ')' || char === ']') depth--;
    else if (char === ',' && depth === 0) {
      selectors.push(prelude.slice(start, i));
      start = i + 1;
    }
  }
  selectors.push(prelude.slice(start));
  return selectors.map((selector) => selector.trim()).filter(Boolean);
}
